import { FormHelperText, MenuItem, Select, Stack } from '@mui/material';
import { useId } from 'react';

import { FormField, isDefined } from 'common';

import { BlockTextArrayField } from './CodeMirror/BlockTextArray';
import { BlockValueTemplateField } from './CodeMirror/FlowTemplate';
import { useSelectedNodeField } from './useSelectedNodeField';
import { useReactFlowViewContext } from '../../context/context';

enum TagsVariant {
  Fixed = 'Fixed',
  Dynamic = 'Dynamic',
}

interface BlockTagsFieldsProps {
  /** Shown under fixed tags (BlockTextArrayField) when provided. */
  helperText?: string;
}

export function BlockTagsFields(props: BlockTagsFieldsProps) {
  const { helperText } = props;
  const modeFieldId = useId();
  const { isEditable } = useReactFlowViewContext();
  const { value: tags, setValue: setTags } = useSelectedNodeField<
    string[] | string | null | undefined
  >('config.tags');

  const inputMode = typeof tags === 'string' ? TagsVariant.Dynamic : TagsVariant.Fixed;

  return (
    <Stack gap={2}>
      <FormField id={modeFieldId} label="Tags type">
        <Select<TagsVariant>
          id={modeFieldId}
          value={inputMode}
          onChange={(event) => {
            const nextMode = event.target.value;
            if (nextMode === inputMode) {
              return;
            }
            if (nextMode === TagsVariant.Fixed) {
              setTags([]);
            } else if (nextMode === TagsVariant.Dynamic) {
              setTags('');
            }
          }}
          disabled={!isEditable}
          inputProps={{ 'aria-label': 'Tags type' }}
          size="small"
          fullWidth
        >
          {Object.values(TagsVariant).map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>
      </FormField>
      {inputMode === TagsVariant.Fixed && (
        <Stack gap={0.5}>
          <BlockTextArrayField
            label="Tags"
            addRowLabel="Add Tag"
            fieldName="config.tags"
            placeholder="Enter tag"
          />
          {isDefined(helperText) && <FormHelperText>{helperText}</FormHelperText>}
        </Stack>
      )}
      {inputMode === TagsVariant.Dynamic && (
        <BlockValueTemplateField
          fieldName="config.tags"
          label="Dynamic tags"
          helperText="Expression that evaluates to a JSON array of tag strings, e.g. {{SomeBlock.data.tags}}"
          placeholder="{{SomeBlock.data.tags}}"
        />
      )}
    </Stack>
  );
}
